import { MidiData, MidiEvent } from 'midi-file';
import { getVoiceValue, VoiceValues } from '../../../common/src/model.ts';
import { sleep } from '../../../common/src/sleep.ts';
import { ensure, ensureExists } from '../../../common/src/util.ts';
import { registerAudioContext } from '../audio/live_audio_contexts.ts';
import AudioBufferLite from './audio_buffer_lite.ts';
import { getTempoMap, getTrackVoice, getVoiceForChannel, TempoEntry } from './midi_notes.ts';

const soundFontPath = '/sound-font.bin';
// Everyone waiting for the soundfont. The boolean says whether the buffers are there: a load that
// failed flushes the queue with false rather than leaving it, so a caller that awaits the soundfont
// learns of the failure instead of waiting forever (issue #3518).
const loadCallbacks: ((loaded: boolean) => void)[] = [];
let loadInitiated = false;
let mp3Buffers: { [noteName: string]: AudioBuffer } | undefined;
const midiNoteNames: string[] = [];
const MaxMidiNoteVelocity = 127;
// The rendered song peaks here, -1 dBFS, rather than at full scale (issue #4003)
const peakCeiling = 0.89;
// A note ramps in over this: the samples start on a step (see audio_buffer_lite.ts)
const noteAttackSec = 0.004;
// The rate decodeAudioFile() resamples every decoded file to (the iPhone's output rate); a decode
// keeps the file's channels whatever the context's channel count
const decodeSampleRate = 48000;
/**
 * Waits for the soundfont buffers: `callback` runs, and the returned promise resolves, with true
 * once they are ready and with false when the load failed (an unreachable /sound-font.bin, a decode
 * that threw). A caller that only wants to be told when the notes are playable can ignore the
 * boolean, as the player's own callers do; a caller that awaits it must handle false, because
 * playNote has nothing to play then.
 */
export function loadInstrument(callback?: (loaded: boolean) => void): Promise<boolean> {
  if (mp3Buffers) {
    callback?.(true);
    return Promise.resolve(true);
  }
  const loaded = new Promise<boolean>((resolve) => {
    loadCallbacks.push((success) => {
      callback?.(success);
      resolve(success);
    });
  });
  void loadSoundfont();
  return loaded;
}

// The browser's Web Audio constructor `name`, or older WebKit's prefixed one
function webAudioConstructor<T>(name: 'AudioContext' | 'OfflineAudioContext'): T {
  const constructor = window[name] || (window as any)[`webkit${name}`];
  if (!constructor) {
    throw new Error(`Web Audio ${name} not supported`);
  }
  return constructor as T;
}

// A real-time context, registered so a heal of the page's frozen clock suspends it with the page's
// (client/src/audio/live_audio_contexts.ts, issue #4116): the page's one context
// (client/src/audio/page_audio_context.ts), which the Player's MIDI path and Learn share, is made
// here, and so is any other real-time context that should take part in heals. Nothing decodes on
// one: decodeAudioFile() decodes offline.
export function createAudioContext(options?: AudioContextOptions): AudioContext {
  const constructor = webAudioConstructor<typeof AudioContext>('AudioContext');
  const context = new constructor(options);
  registerAudioContext(context);
  return context;
}

// Created on the first decode and kept: it never renders, so it holds nothing
let decodeContext: OfflineAudioContext | undefined;

// Decodes an encoded audio file (the soundfont's notes, the vocals' MP3s) on an OfflineAudioContext
// of one channel and one frame, never started, and so never registered (issue #4116): a real-time
// AudioContext drives WebKit's output unit, which every real-time context of the page shares, from
// the moment it is created, and one left running for a decode kept that unit from stopping when a
// heal suspended the page's context, so the heal restarted nothing. An offline context has no
// output. The buffer comes out at decodeSampleRate, and an AudioBuffer plays on any context, one at
// another sample rate resampled as it plays.
export function decodeAudioFile(data: ArrayBuffer): Promise<AudioBuffer> {
  if (!decodeContext) {
    const constructor = webAudioConstructor<typeof OfflineAudioContext>('OfflineAudioContext');
    decodeContext = new constructor(1, 1, decodeSampleRate);
  }
  return decodeContext.decodeAudioData(data);
}

// The soundfont's name for a MIDI note number ('A0' for 21 up to 'C8' for 108), undefined outside
// the 88 keys the soundfont holds
export function getMidiNoteName(noteNumber: number): string | undefined {
  if (midiNoteNames.length === 0) {
    generateMidiNoteNames();
  }
  return midiNoteNames[noteNumber];
}

export function generateMidiNoteNames() {
  // note names look like 'A0', 'Bb0', 'B0', 'C1', 'Db1', etc.
  // they wrap when going from B to C
  // see https://www.inspiredacoustics.com/en/MIDI_note_numbers_and_center_frequencies
  const midiLetters = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'];
  const startingNumberOffset = 21;
  const startingLetterIndex = 9;
  const pianoKeyCount = 88;
  for (let pianoKey = 0; pianoKey < pianoKeyCount; pianoKey += 1) {
    const noteNumber = startingNumberOffset + pianoKey;
    const midiOctave = Math.floor((pianoKey + startingLetterIndex) / midiLetters.length);
    const letter = midiLetters[(startingLetterIndex + pianoKey) % midiLetters.length];
    midiNoteNames[noteNumber] = letter + midiOctave.toString();
  }
}

// The decoded sample for a soundfont note name, undefined before the soundfont has loaded or for a
// name it does not hold; the Player's Web Audio path builds its own nodes from it
export function getSoundfontBuffer(noteName: string): AudioBuffer | undefined {
  return mp3Buffers?.[noteName];
}

export interface ActiveNote {
  gainNode: GainNode;
  gainValue: number;
}

/**
 * must call loadInstrument first and wait for the callback before calling playNote
 *
 * `output` is where the note goes: the context's speakers unless the caller routes it elsewhere (the
 * Learn audio session's guide bus, which also feeds the microphone capture its reference)
 */
export function playNote(
  audioContext: AudioContext,
  noteName: string,
  gain: number,
  when: number,
  output: AudioNode = audioContext.destination,
): ActiveNote {
  ensure(noteName in ensureExists(mp3Buffers), `Note "${noteName}" not found in mp3_buffers`);
  const buffer = ensureExists(mp3Buffers)[noteName];
  const source = audioContext.createBufferSource();
  const gainNode = audioContext.createGain();
  source.buffer = buffer;
  source.connect(gainNode);
  gainNode.connect(output);
  gainNode.gain.setValueAtTime(0, when);
  gainNode.gain.linearRampToValueAtTime(gain, when + noteAttackSec);
  source.start(when);
  return { gainNode, gainValue: gain };
}

export function stopNote({ gainNode, gainValue }: ActiveNote, when: number): any {
  if (gainValue > 0) {
    // quick fade better than sudden stop (causes pop if stopping sign wave at non-zero location)
    // fade begins at previous event (which explains the duplicate setValueAtTime)
    gainNode.gain.setValueAtTime(gainValue, when);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, when + 1 / 8);
  }
}

export enum MP3GeneratorResult {
  Success = 'success',
  Waiting = 'waiting',
  Failure = 'failure',
  Abort = 'abort',
}

interface MP3Note {
  buffer: AudioBuffer;
  startSec: number;
  durationSec: number;
  volumeMultiplier: number;
}

export interface MP3Inputs {
  tempo: number;
  modulationHalfSteps: number;
  voiceValues: VoiceValues;
}

//
// PERFORMANCE NOTE
//
// Generating the audio blob file causes the UI thread to block
// Surprisingly, the blocking has very little to do with the code below
// It appears to be garbage collection, triggered by returning the mp3Url
// If we still do all the same work but don't return the URL, everything is much smoother,
// even if the caller doesn't use the returned URL - just returning it causes the blockage on iOS.
// Difficult to profile locally b/c the desktop is so much faster & has a lot of memory
// The local profiler points to export as causing the blockage
export async function generateWavBlobForMIDI(
  midi: MidiData,
  { tempo, modulationHalfSteps, voiceValues }: MP3Inputs,
  shouldAbort: () => boolean,
): Promise<{
  audioBlob?: Blob;
  result: MP3GeneratorResult;
}> {
  // todo(hewitt): how do we not leak previous audio buffers when creating new ones?

  if (!mp3Buffers || tempo === 0) {
    if (!mp3Buffers) {
      console.error(`Sound font failed to load before MP3 generation started`);
    } else {
      console.error('Zero tempo during MP3 generation');
    }
    return { result: MP3GeneratorResult.Waiting };
  }

  const { ticksPerBeat } = midi.header;
  if (!ticksPerBeat) {
    console.error(`Midi: Missing ticksPerBeat in midi header`);
    return { result: MP3GeneratorResult.Failure };
  }
  try {
    const notes: MP3Note[] = [];
    let totalDurationSec = 0;
    const tracksWithNotes = midi.tracks.filter((track) => track.some((event) => event.type === 'noteOn'));
    const fileTempo = getTempoMap(midi, 1)[0].tempo;
    const tempoScale = tempo / fileTempo;
    const tempoMap = getTempoMap(midi, tempoScale);

    for (const track of tracksWithNotes) {
      const { notes: channelNotes, durationSec } = getMP3NotesFromMidiEvents(
        track,
        ticksPerBeat,
        tempoMap,
        modulationHalfSteps,
        voiceValues,
      );
      notes.push(...channelNotes);
      totalDurationSec = Math.max(totalDurationSec, durationSec);
    }

    const { numberOfChannels, sampleRate } = mp3Buffers['C4'];
    const buffer = new AudioBufferLite(numberOfChannels, sampleRate, totalDurationSec);
    for (const [index, note] of notes.entries()) {
      const { buffer: noteBuffer, startSec, durationSec, volumeMultiplier } = note;
      buffer.recordNote(noteBuffer, startSec, durationSec, volumeMultiplier);
      if (index % 20 === 0) {
        // yield to UI events
        await sleep(0);
        if (shouldAbort()) {
          return { result: MP3GeneratorResult.Abort };
        }
      }
    }
    await buffer.normalize({ normalizationTarget: peakCeiling * Math.max(...Object.values(voiceValues)) });
    return { audioBlob: buffer.exportAsWavBlob(), result: MP3GeneratorResult.Success };
  } catch (e: any) {
    console.log(e.message);
    return { result: MP3GeneratorResult.Failure };
  }
}

export async function loadSoundfont() {
  if (!loadInitiated) {
    try {
      loadInitiated = true;
      const response = await fetch(soundFontPath);
      const soundFont = await response.arrayBuffer();
      // Decoded offline (see decodeAudioFile): the page's shared context is created by whoever
      // plays first, inside that gesture and after the page audio session is declared, and the
      // soundfont load at startup must not create it early (issue #3718), nor any other real-time
      // context, which would keep WebKit's shared output unit running through a heal of the page's
      // frozen clock (issue #4116).
      const decoded = await Promise.all(
        readSoundFont(soundFont).map(({ noteName, data }) =>
          decodeAudioFile(data).then((buffer) => ({ noteName, buffer })),
        ),
      );
      const buffers: { [noteName: string]: AudioBuffer } = {};
      for (const { noteName, buffer } of decoded) {
        buffers[noteName] = buffer;
      }
      mp3Buffers = buffers;
      flushLoadCallbacks(true);
    } catch (error: any) {
      loadInitiated = false;
      console.error(`Failed to load sound font: ${error.message}`);
      // The error stays swallowed for the callers that only asked to be told when the notes are
      // ready, but the queue is flushed either way: a caller awaiting the soundfont (the Learn
      // exercises' piano guide) would otherwise wait for a callback that never comes.
      flushLoadCallbacks(false);
    }
  }
}

// The soundfont file's notes: its note count, then for each note its name in four bytes, the length
// of its encoded audio and that audio. Throws on a file whose lengths run past its end.
function readSoundFont(soundFont: ArrayBuffer): { noteName: string; data: ArrayBuffer }[] {
  const dataView = new DataView(soundFont);
  const noteCount = dataView.getUint32(0);
  const noteNameSize = 4;
  const uint32Size = 4;
  let currentOffset = uint32Size;
  const notes = [];
  for (let noteIndex = 0; noteIndex < noteCount; noteIndex++) {
    const noteName = new TextDecoder().decode(new DataView(soundFont, currentOffset, noteNameSize)).trim();
    currentOffset += noteNameSize;
    const dataLength = dataView.getUint32(currentOffset);
    currentOffset += uint32Size;
    notes.push({ noteName, data: soundFont.slice(currentOffset, currentOffset + dataLength) });
    currentOffset += dataLength;
    ensure(
      currentOffset <= soundFont.byteLength,
      `Expected currentOffset (${currentOffset}) <= soundFontLength (${soundFont.byteLength})`,
    );
  }
  return notes;
}

// Takes the queue before calling anything, so a callback that asks for the soundfont again queues
// itself for the next attempt rather than being called by this one
function flushLoadCallbacks(loaded: boolean) {
  for (const callback of loadCallbacks.splice(0, loadCallbacks.length)) {
    callback(loaded);
  }
}

type NoteNumber = number;

function getMP3NotesFromMidiEvents(
  events: MidiEvent[], // include non-note events such as lyric events, which can contain deltaTime offsets
  ticksPerBeat: number,
  tempoMap: TempoEntry[],
  modulationHalfSteps: number,
  voiceValues: VoiceValues,
): {
  notes: MP3Note[];
  durationSec: number;
} {
  const notes: MP3Note[] = [];
  const activeNotes = new Map<NoteNumber, MP3Note>();
  let currentTimeSec = 0;
  let absoluteTick = 0;
  let tempoMapIndex = 0;
  const trackVoice = getTrackVoice(events);

  for (const [_index, event] of events.entries()) {
    const { deltaTime } = event;
    // Accumulate time, advancing through tempo map entries as needed
    let remainingTicks = deltaTime;
    while (tempoMapIndex < tempoMap.length - 1 && remainingTicks > 0) {
      const nextTempoTick = tempoMap[tempoMapIndex + 1].tick;
      const ticksUntilNextTempo = nextTempoTick - absoluteTick;
      if (remainingTicks < ticksUntilNextTempo) {
        break;
      }
      currentTimeSec += (ticksUntilNextTempo / ticksPerBeat / tempoMap[tempoMapIndex].tempo) * 60;
      absoluteTick = nextTempoTick;
      remainingTicks -= ticksUntilNextTempo;
      tempoMapIndex++;
    }
    currentTimeSec += (remainingTicks / ticksPerBeat / tempoMap[tempoMapIndex].tempo) * 60;
    absoluteTick += remainingTicks;
    if (event.type !== 'noteOn' && event.type !== 'noteOff') {
      continue;
    }
    const { channel, noteNumber, velocity } = event;
    const voice = trackVoice ?? getVoiceForChannel(channel);
    const voiceValue = getVoiceValue(voice, voiceValues);
    const noteName = midiNoteNames[noteNumber + modulationHalfSteps];
    if (event.type === 'noteOn') {
      if (activeNotes.has(noteNumber)) {
        // TODO(hewitt): Bring these back once Coda bug is fixed and test in generate_midi.test.ts is passing
        // console.error(`Missing note off event for ${noteNumber} before next note on event`);
      } else {
        const noteVelocity = velocity / MaxMidiNoteVelocity;
        const note = {
          buffer: ensureExists(mp3Buffers)[noteName],
          startSec: currentTimeSec,
          durationSec: 0,
          volumeMultiplier: noteVelocity * voiceValue,
        };
        notes.push(note);
        activeNotes.set(noteNumber, note);
      }
    } else {
      const firstEventIsNoteOff = notes.length === 0;
      if (firstEventIsNoteOff) {
        continue;
      }
      const note = activeNotes.get(noteNumber);
      if (!note) {
        // TODO(hewitt): Bring these back once Coda bug is fixed and test in generate_midi.test.ts is passing
        // console.error(`Missing note on event for ${noteNumber} before note off event`);
      } else {
        note.durationSec = currentTimeSec - note.startSec;
        activeNotes.delete(noteNumber);
      }
    }
  }
  return { notes, durationSec: currentTimeSec };
}
